import React, {useState} from "react";
import {BarcodeFormat} from "@zxing/library";
import apiFetch from "./apiClient";
import {CameraScanner} from "./shared/CameraScanner";
import {Upload, Link as LinkIcon, Camera, Loader, Save, Plus, X, CheckCircle, QrCode} from 'lucide-react'

const fieldStyle = {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    color: 'var(--color-text-primary)',
    borderRadius: '0.375rem'
};
const fieldFocusHandlers = {
    onFocus: (e) => e.target.style.borderColor = 'var(--color-primary)',
    onBlur: (e) => e.target.style.borderColor = 'var(--color-border)'
};

interface Product {
        id: string;
        name: string;
        price: string;
        quantity: string;
        unit: string;
        payed: string;
        gtin: string;
}

const UploadReceipt = () => {
    // const [uploadMethod, setUploadMethod] = useState<"image"|"url"|null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [receiptData, setReceiptData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [receiptUrl, setReceiptUrl] = useState("");
    const [error, setError] = useState("");
    const [category, setCategory] = useState("");

    const processReceipt = async (method: "image" | "url", file, urlOverride) => {
        setIsProcessing(true);
        setError("");
        if (method === "image" && file) {
            const formData = new FormData();
            formData.append("file", file);

            try {
                const response = await apiFetch("/api/receiptcontrol/upload-receipt", {
                    method: "POST",
                    body: formData,
                });
                setReceiptData(response);
                handleCategory();
            } catch (err) {
                setError(err.message);
                console.log(error);
            } finally {
                setIsProcessing(false);
            }
        } else if (method === "url" && (urlOverride || receiptUrl)) {
            try {
                const response = await apiFetch("/api/receiptcontrol/receipt-url", {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({'invoiceurl': urlOverride || receiptUrl}),
                });
                const formattedData = {
                    ...response, // Copy all properties from the API result
                    date: formatApiDateForInput(response.date) // Overwrite the 'date' with the formatted version
                };
                setReceiptData(formattedData);
                handleCategory();
            } catch (err) {
                setError(err.message);
                console.log(error);
            } finally {
                setIsProcessing(false);
            }
        } else {
            // Segurança: nunca deixar o spinner girando pra sempre se nenhum
            // dos dois ramos acima bater (ex: file/url ausente por algum motivo).
            setIsProcessing(false);
        }
    };
    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            processReceipt("image", file);
        }
    };
    const handleUrlSubmit = () => {
        if(receiptUrl.trim()){
            processReceipt("url");
        }
    };
    const handleQrDetected = (text) => {
        setReceiptUrl(text);
        processReceipt("url", null, text);
    };
    const handleSave = async (e) => {
        setIsSaving(true);
        try {
            const response = await apiFetch("/api/receiptcontrol/save-receipt-products", {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(receiptData),
            });
            console.log("Save response:", response);
            console.log("Receipt data sent:", receiptData);
            setIsSaving(false);
            setSaveSuccess(true);
            // Reset after 3 seconds
            setTimeout(() => {
                setSaveSuccess(false);
                resetForm();
            }, 3000);
        } catch (err) {
            setError(err.message);
            setIsSaving(false);
        }
    };
    const resetForm = () => {
        // setUploadMethod(null);
        setReceiptData(null);
        setSelectedFile(null);
        setReceiptUrl("");
    };
    const updateProduct = (id, field, value) => {
        
        if(receiptData){
            setReceiptData({
                ...receiptData,
                products:receiptData.products.map(p => 
                    p.id === id ? {...p,[field]:value}:p
                )
            });
        }
    };
    const addProduct = () => {
        if(receiptData){
            const newProduct: Product = {
                id: Date.now().toString(),
                name:"",
                price:"",
                quantity:"",
                unit:"unit",
                payed:"",
                gtin:""
            };
            setReceiptData({
                ...receiptData,
                products:[...receiptData.products,newProduct]
            });
        }
    };
    const removeProduct = (id:string) => {
        if(receiptData){
            setReceiptData({
                ...receiptData,
                products:receiptData.products.filter(p => p.id !== id)
            });
        }
    };
    const formatApiDateForInput = (ddmmyyyyDate) => {
        if (!ddmmyyyyDate || typeof ddmmyyyyDate !== 'string') {
            return ''; // Return empty if date is invalid
        }

        const parts = ddmmyyyyDate.split('/');
        if (parts.length !== 3) {
            return ''; // Return empty for incorrect format
        }

        const [day, month, year] = parts;
        return `${year}-${month}-${day}`;
    };
    const handleCategory = async (e) => {
        try {
            const response = await apiFetch("/api/financecontrol/get-categories", {
                method: "GET",
            });
            setCategory(response);
        } catch (err) {
            setError(err.message);
            console.log(error);
        } finally {
            setIsProcessing(false);
        }
    }

    if(saveSuccess) {
        return (
            <div className="p-4">
                <div className="container-fluid" style={{ maxWidth: '1400px' }}>
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-8 col-lg-6">
                            <div className="card custom-card text-center p-5">
                                <div className="mb-4">
                                    <div 
                                        className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                        style={{ 
                                            width: '80px', 
                                            height: '80px', 
                                            backgroundColor: 'rgba(0, 255, 136, 0.2)' 
                                        }}
                                    >
                                        <CheckCircle size={48} className="text-primary-custom" />
                                    </div>
                                </div>
                                <h2 className="h3 fw-bold mb-3">Nota computada com sucesso!</h2>
                                <p className="text-muted-custom mb-0">
                                    Sua nota foi computada e salva em sua conta
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-4">
            <div className="container-fluid" style={{ maxWidth: '1400px' }}>
                {!receiptData ? (
                    <div className="row g-4">
                        {error && (
                            <div className="col-12">
                                <p className="text-danger-custom small mb-0">{error}</p>
                            </div>
                        )}
                        {/* Upload Image */}
                        <div className="col-12 col-lg-6">
                            <div className="card custom-card h-100">
                                <div className="car-body p-4">
                                    <div className="d-flex align-items-center mb-4 gap-3">
                                        <div className="icon-container" style={{ 
                                            backgroundColor: 'rgba(0, 255, 136, 0.2)', 
                                            width: '48px', 
                                            height: '48px'
                                        }}>
                                            <Camera size={24} className="text-primary-custom" />    
                                        </div>
                                        <div>
                                            <h3 className="h5 fw-semibold mb-1">Enviar Imagem</h3>
                                            <p className="small text-muted-custom mb-0">Tire uma foto ou envie do seu dispositivo</p>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="border border-2 rounded p-5 text-center"
                                            style={{ borderColor: 'var(--color-border)', borderStyle: 'dashed' }}
                                        >
                                            <Upload size={48} className="text-muted-custom mb-3" />
                                            <p className="mb-2">Arraste a imagem do recibo aqui</p>
                                            <p className="small text-muted-custom mb-4">ou clique para procurar</p>
                                            <label htmlFor="file-upload" className="btn btn-primary-custom w-100">
                                                <input
                                                    id="file-upload"
                                                    type="file"
                                                    accept="image/*"
                                                    capture="environment"
                                                    className="d-none"
                                                    onChange={handleImageUpload}
                                                    disabled={isProcessing}
                                                />
                                                {isProcessing ? (
                                                    <span className="d-flex align-items-center justify-content-center gap-2">
                                                        <Loader size={16} className="spinner-border spinner-border-sm" />
                                                        Processando...
                                                    </span>
                                                ) : (
                                                    <span className="d-flex align-items-center justify-content-center gap-2">
                                                        <Camera size={16} />
                                                        Selecionar Imagem
                                                    </span>
                                                )}
                                            </label>

                                            {selectedFile && !isProcessing &&(
                                                <div className="mt-3">
                                                    <p className="small text-primary-custom mb-0">Selecionado {selectedFile.name}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-4">
                                            <p className="small text-muted-custom mb-2">Formatos suportados:</p>
                                            <div className="d-flex gap-2 flex-wrap">
                                                <span className="badge badge-secondary-custom">JPG</span>
                                                <span className="badge badge-secondary-custom">PNG</span>
                                                <span className="badge badge-secondary-custom">HEIC</span>
                                                <span className="badge badge-secondary-custom">PDF</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Upload URL */}
                        <div className="col-12 col-lg-6">
                            <div className="card custom-card h-100">
                                <div className="car-body p-4">
                                    <div className="d-flex align-items-center mb-4 gap-3">
                                        <div className="icon-container" style={{ 
                                            backgroundColor: 'rgba(0, 255, 136, 0.2)', 
                                            width: '48px',
                                            height: '48px'
                                        }}>
                                            <LinkIcon size={24} className="text-primary-custom" />
                                        </div>
                                        <div>
                                            <h3 className="h5 fw-semibold mb-1">Importar da URL</h3>
                                            <p className="small text-muted-custom mb-0">Cole o link do seu recibo</p>
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="receipt-url" className="form-label text-muted-custom small">URL do Recibo</label>
                                        <input
                                            id="receipt-url"
                                            type="url"
                                            className="form-control mb-3"
                                            placeholder="https://example.com/receipt.jpg"
                                            value={receiptUrl}
                                            onChange={(e) => setReceiptUrl(e.target.value)}
                                            disabled={isProcessing}
                                            style={{
                                                backgroundColor: 'var(--color-surface)',
                                                border: '1px solid var(--color-border)',
                                                color: 'var(--color-text-primary)',
                                                padding: '0.75rem 1rem',
                                                borderRadius: '0.5rem'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                            onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                        />

                                        <div className="mb-3">
                                            <CameraScanner
                                                onDetected={handleQrDetected}
                                                formats={[BarcodeFormat.QR_CODE]}
                                                label="Ler QR Code da Nota"
                                                icon={QrCode}
                                                scanningHint="Aponte a câmera para o QR Code da nota."
                                                closeAriaLabel="Fechar leitor de QR Code"
                                            />
                                        </div>

                                        <button
                                            className="btn btn-primary-custom w-100"
                                            onClick={handleUrlSubmit}
                                            disabled={!receiptUrl.trim() || isProcessing}
                                        >
                                            {isProcessing ? (
                                                <span className="d-flex align-items-center justify-content-center gap-2">
                                                    <Loader size={16} className="spinner-border spinner-border-sm" />
                                                    Processando...
                                                </span>
                                            ) : (
                                                <span className="d-flex align-items-center justify-content-center gap-2">
                                                    <LinkIcon size={16} />
                                                    Processar URL
                                                </span>
                                            )}
                                        </button>
                                    </div>
                                    <div className="mt-4 p-3 rounded">
                                        <p className="small text-muted-custom mb-2">
                                            <strong className="">Dica:</strong> Você pode usar URLs de:
                                        </p>
                                        <ul className="small text-muted-custom mb-0 ps-3">
                                            <li>Recibos por e-mail</li>
                                            <li>Armazenamento em nuvem (Dropbox, Google Drive)</li>
                                            <li>Pedidos de compras online</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="row g-4">
                        {/* Store Information Card */}
                        <div className="col-12">
                            <div className="card custom-card">
                                <div className="card-body p-4">
                                    <h3 className="h5 fw-semibold mb-4">Informações</h3>
                                    <div className="row g-3">
                                        <div className="col-12 col-md-4">
                                            <label className="form-label text-muted-custom small">Nome da Loja</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={receiptData.store_name}
                                                onChange={(e) => setReceiptData({ ...receiptData, store_name: e.target.value })}
                                                style={{
                                                    backgroundColor: 'var(--color-surface)',
                                                    border: '1px solid var(--color-border)',
                                                    color: 'var(--color-text-primary)',
                                                    padding: '0.75rem 1rem',
                                                    borderRadius: '0.5rem'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                            />
                                        </div>
                                        <div className="col-12 col-md-4">
                                            <label className="form-label text-muted-custom small">Data da Compra</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={receiptData.date}
                                                onChange={(e) => setReceiptData({ ...receiptData, date: e.target.value })}
                                                style={{
                                                    backgroundColor: 'var(--color-surface)',
                                                    border: '1px solid var(--color-border)',
                                                    color: 'var(--color-text-primary)',
                                                    padding: '0.75rem 1rem',
                                                    borderRadius: '0.5rem'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                            />
                                        </div>
                                        <div className="col-12 col-md-4">
                                                <label className="form-label text-muted-custom small">Categoria</label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={category ? category.categoria_id : ''}
                                                    onChange={(e) => setReceiptData({...receiptData, category: e.target.value})}
                                                    style={{
                                                        backgroundColor: 'var(--color-surface)',
                                                        border: '1px solid var(--color-border)',
                                                        color: 'var(--color-text-primary)',
                                                        padding: '0.75rem 1rem',
                                                        borderRadius: '0.5rem'
                                                    }}
                                                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                                >
                                                    <option value="">Selecione</option>
                                                    {category.data && category.data.filter(cat => cat.categoria_tipo === 'debito').map((cat) => (
                                                        <option key={cat.CATEGORIA_ID} value={cat.categoria_id}>{cat.categoria_nome}</option>
                                                    ))}
                                                </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Products Table */}
                        <div className="col-12">
                            <div className="card custom-card">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center justify-content-between mb-4">
                                        <h3 className="h5 fw-semibold mb-0">Produtos</h3>
                                        <button 
                                            className="btn btn-primary-custom d-flex align-items-center gap-2"
                                            onClick={addProduct}
                                        >
                                            <Plus size={16} />
                                            Adicionar Produto
                                        </button>
                                    </div>

                                    <div className="d-none d-md-block table-responsive">
                                        <table className="table table-custom table-hover mb-0">
                                            <thead>
                                                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                                                    <th className="text-muted-custom small fw-medium" style={{ borderBottom: 'none' }}>Nome</th>
                                                    <th className="text-muted-custom small fw-medium" style={{ borderBottom: 'none' }}>GTIN</th>
                                                    <th className="text-muted-custom small fw-medium" style={{ borderBottom: 'none' }}>Preço</th>
                                                    <th className="text-muted-custom small fw-medium" style={{ borderBottom: 'none' }}>Quantidade</th>
                                                    <th className="text-muted-custom small fw-medium" style={{ borderBottom: 'none' }}>Unidade</th>
                                                    <th className="text-muted-custom small fw-medium" style={{ borderBottom: 'none' }}>Pago</th>
                                                    <th className="text-muted-custom small fw-medium text-end" style={{ borderBottom: 'none' }}>Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {receiptData.products && receiptData.products.map((product) => (
                                                    <tr key={product.id} style={{ borderBottom: '1px solid rgba(45, 55, 72, 0.3)' }}>
                                                        <td style={{ borderBottom: 'none' }}>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={product.name}
                                                                onChange={(e) => updateProduct(product.id, 'name', e.target.value)}
                                                                style={{
                                                                    backgroundColor: 'var(--color-surface)',
                                                                    border: '1px solid var(--color-border)',
                                                                    color: 'var(--color-text-primary)',
                                                                    borderRadius: '0.375rem'
                                                                }}
                                                                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                                                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                                            />
                                                        </td>
                                                        <td style={{ borderBottom: 'none', width: '140px' }}>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="—"
                                                                value={product.gtin || ''}
                                                                onChange={(e) => updateProduct(product.id, 'gtin', e.target.value)}
                                                                style={{
                                                                    backgroundColor: 'var(--color-surface)',
                                                                    border: '1px solid var(--color-border)',
                                                                    color: 'var(--color-text-primary)',
                                                                    borderRadius: '0.375rem'
                                                                }}
                                                                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                                                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                                            />
                                                        </td>
                                                        <td style={{ borderBottom: 'none', width: '120px' }}>
                                                            <div className="input-group input-group-sm">
                                                                <span
                                                                    className="input-group-text"
                                                                    style={{
                                                                        backgroundColor: 'var(--color-surface)',
                                                                        border: '1px solid var(--color-border)',
                                                                        borderRight: 'none',
                                                                        color: 'var(--color-text-secondary)' 
                                                                    }}
                                                                >
                                                                    R$
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    className="form-control form-control-sm"
                                                                    value={product.price}
                                                                    onChange={(e) => updateProduct(product.id, 'price', e.target.value)}
                                                                    style={{
                                                                        backgroundColor: 'var(--color-surface)',
                                                                        border: '1px solid var(--color-border)',
                                                                        borderLeft: 'none',
                                                                        color: 'var(--color-text-primary)',
                                                                        borderRadius: '0 0.375rem 0.375rem 0'
                                                                    }}
                                                                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                                                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td style={{ borderBottom: 'none', width: '100px' }}>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="form-control form-control-sm"
                                                                value={product.quantity}
                                                                onChange={(e) => updateProduct(product.id, 'quantity', e.target.value)}
                                                                style={{
                                                                    backgroundColor: 'var(--color-surface)',
                                                                    border: '1px solid var(--color-border)',
                                                                    color: 'var(--color-text-primary)',
                                                                    borderRadius: '0.375rem'
                                                                }}
                                                                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                                                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                                            />
                                                        </td>
                                                        <td style={{ borderBottom: 'none', width: '120px' }}>
                                                            <select
                                                                className="form-select form-select-sm"
                                                                // value={product.unit}
                                                                value={product ? product.unit : ''}
                                                                onChange={(e) => updateProduct(product.id, 'unit', e.target.value)}
                                                                style={{
                                                                    backgroundColor: 'var(--color-surface)',
                                                                    border: '1px solid var(--color-border)',
                                                                    color: 'var(--color-text-primary)',
                                                                    borderRadius: '0.375rem'
                                                                }}
                                                                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                                                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                                            >
                                                                <option value="">Selecionar</option>
                                                                <option value="unit">unidade</option>
                                                                <option value="kg">kg</option>
                                                                <option value="g">g</option>
                                                                <option value="L">L</option>
                                                                <option value="mL">mL</option>
                                                                <option value="lb">lb</option>
                                                                <option value="oz">oz</option>
                                                            </select>
                                                        </td>
                                                        <td style={{ borderBottom: 'none', width: '120px' }}>
                                                            <div className="input-group input-group-sm">
                                                                <span 
                                                                    className="input-group-text" 
                                                                    style={{ 
                                                                        backgroundColor: 'var(--color-surface)', 
                                                                        border: '1px solid var(--color-border)',
                                                                        borderRight: 'none',
                                                                        color: 'var(--color-text-secondary)' 
                                                                    }}
                                                                >
                                                                    R$ 
                                                                </span>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    className="form-control form-control-sm"
                                                                    value={product.payed}
                                                                    onChange={(e) => updateProduct(product.id, 'payed', e.target.value)}
                                                                    style={{
                                                                        backgroundColor: 'var(--color-surface)',
                                                                        border: '1px solid var(--color-border)',
                                                                        borderLeft: 'none',
                                                                        color: 'var(--color-text-primary)',
                                                                        borderRadius: '0 0.375rem 0.375rem 0'
                                                                    }}
                                                                    onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                                                                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                                                                />
                                                            </div>
                                                        </td>
                                                        <td className="text-end" style={{ borderBottom: 'none', width: '80px' }}>
                                                            <button
                                                                className="btn btn-sm"
                                                                onClick={() => removeProduct(product.id)}
                                                                style={{
                                                                    backgroundColor: 'transparent',
                                                                    border: '1px solid var(--color-danger)',
                                                                    color: 'var(--color-danger)',
                                                                    borderRadius: '0.375rem'
                                                                }}
                                                                aria-label="Remover produto"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="d-md-none d-flex flex-column gap-3">
                                        {receiptData.products && receiptData.products.map((product) => (
                                            <div key={product.id} className="custom-card p-3">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <span className="text-muted-custom small fw-medium">Item</span>
                                                    <button
                                                        className="btn btn-sm"
                                                        onClick={() => removeProduct(product.id)}
                                                        style={{
                                                            backgroundColor: 'transparent',
                                                            border: '1px solid var(--color-danger)',
                                                            color: 'var(--color-danger)',
                                                            borderRadius: '0.375rem'
                                                        }}
                                                        aria-label="Remover produto"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>

                                                <div className="mb-2">
                                                    <label className="form-label text-muted-custom small mb-1">Nome</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={product.name}
                                                        onChange={(e) => updateProduct(product.id, 'name', e.target.value)}
                                                        style={fieldStyle}
                                                        {...fieldFocusHandlers}
                                                    />
                                                </div>

                                                <div className="row g-2 mb-2">
                                                    <div className="col-6">
                                                        <label className="form-label text-muted-custom small mb-1">GTIN</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="—"
                                                            value={product.gtin || ''}
                                                            onChange={(e) => updateProduct(product.id, 'gtin', e.target.value)}
                                                            style={fieldStyle}
                                                            {...fieldFocusHandlers}
                                                        />
                                                    </div>
                                                    <div className="col-6">
                                                        <label className="form-label text-muted-custom small mb-1">Unidade</label>
                                                        <select
                                                            className="form-select"
                                                            value={product ? product.unit : ''}
                                                            onChange={(e) => updateProduct(product.id, 'unit', e.target.value)}
                                                            style={fieldStyle}
                                                            {...fieldFocusHandlers}
                                                        >
                                                            <option value="">Selecionar</option>
                                                            <option value="unit">unidade</option>
                                                            <option value="kg">kg</option>
                                                            <option value="g">g</option>
                                                            <option value="L">L</option>
                                                            <option value="mL">mL</option>
                                                            <option value="lb">lb</option>
                                                            <option value="oz">oz</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="row g-2">
                                                    <div className="col-6">
                                                        <label className="form-label text-muted-custom small mb-1">Quantidade</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            className="form-control"
                                                            value={product.quantity}
                                                            onChange={(e) => updateProduct(product.id, 'quantity', e.target.value)}
                                                            style={fieldStyle}
                                                            {...fieldFocusHandlers}
                                                        />
                                                    </div>
                                                    <div className="col-6">
                                                        <label className="form-label text-muted-custom small mb-1">Preço</label>
                                                        <div className="input-group">
                                                            <span className="input-group-text" style={{ ...fieldStyle, borderRight: 'none' }}>R$</span>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                className="form-control"
                                                                value={product.price}
                                                                onChange={(e) => updateProduct(product.id, 'price', e.target.value)}
                                                                style={{ ...fieldStyle, borderLeft: 'none' }}
                                                                {...fieldFocusHandlers}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mt-2">
                                                    <label className="form-label text-muted-custom small mb-1">Pago</label>
                                                    <div className="input-group">
                                                        <span className="input-group-text" style={{ ...fieldStyle, borderRight: 'none' }}>R$</span>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            className="form-control"
                                                            value={product.payed}
                                                            onChange={(e) => updateProduct(product.id, 'payed', e.target.value)}
                                                            style={{ ...fieldStyle, borderLeft: 'none' }}
                                                            {...fieldFocusHandlers}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-4 p-3 rounded d-flex align-items-center justify-content-between"
                                        style={{ backgroundColor: 'rgba(37, 42, 58, 0.3)' }}
                                    >
                                        <div>
                                            <p className="text-muted-custom small mb-1">Total de Itens</p>
                                            <p className="h4 fw-bold mb-0">{receiptData.products.length}</p>
                                        </div>
                                        <div>
                                            <p className="text-muted-custom small mb-1">Total Pago</p>
                                            <p className="h4 fw-bold text-primary-custom mb-0">
                                                ${receiptData.total_taxes}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-12">
                            <div className="d-flex gap-3 justify-content-end">
                                <button 
                                    className="btn btn-outline-custom px-4"
                                    onClick={resetForm}
                                    disabled={isSaving}
                                >
                                    Cancelar
                                </button>
                                <button 
                                    className="btn btn-primary-custom px-5"
                                    onClick={handleSave}
                                    disabled={isSaving}
                                >
                                    {isSaving ? (
                                        <span className="d-flex align-items-center gap-2">
                                            <Loader size={16} className="spinner-border spinner-border-sm" />
                                            Salvando...
                                        </span>
                                    ) : (
                                        <span className="d-flex align-items-center gap-2">
                                            <Save size={16} />
                                            Nota salva
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UploadReceipt;